<template>
  <footer class="border-t border-bochas-card-border px-6 py-10 lg:px-16 lg:py-12">
    <div
      class="mx-auto grid max-w-7xl grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-[1.1fr_0.9fr_1.1fr_1.2fr]"
    >
      <div class="min-w-0">
        <h3 class="mb-4 text-2xl font-bold text-white">{{ data.about?.title }}</h3>
        <p class="max-w-sm text-base font-normal leading-relaxed text-white">
          {{ data.about?.description }}
        </p>
      </div>

      <div class="min-w-0">
        <h3 class="mb-4 text-2xl font-medium text-white">Enlaces Rápidos</h3>
        <ul class="space-y-3 text-base font-normal text-bochas-text-muted">
          <li v-for="link in quickLinks" :key="link.label">
            <router-link :to="link.to" class="footer-link text-white">{{ link.label }}</router-link>
          </li>
        </ul>
      </div>

      <div v-if="institutionalLinks.length" class="min-w-0">
        <h3 class="mb-4 text-2xl font-medium text-white">Institucional</h3>
        <ul class="space-y-3 text-base font-normal text-bochas-text-muted">
          <li v-for="item in institutionalLinks" :key="item.label">
            <a
              v-if="item.url"
              :href="item.url"
              target="_blank"
              rel="noopener"
              class="footer-link text-white flex items-center gap-2"
            >
              <IconFbochas :name="item.icon" :size="14" />
              {{ item.label }}
            </a>
            <router-link
              v-else
              :to="item.to"
              class="footer-link text-white flex items-center gap-2"
            >
              <IconFbochas :name="item.icon" :size="14" />
              {{ item.label }}
            </router-link>
          </li>
        </ul>
      </div>

      <div class="min-w-0">
        <h3 class="mb-4 text-2xl font-medium text-white">Contacto</h3>
        <ul class="space-y-3 text-base font-normal text-white">
          <li class="flex items-start gap-2">
            <IconFbochas name="location" :size="14" class="mt-1 flex-shrink-0" />
            <a
              :href="`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.contact?.address || '')}`"
              target="_blank"
              rel="noopener noreferrer"
              class="footer-link min-w-0 leading-relaxed"
            >
              {{ data.contact?.address }}
            </a>
          </li>
          <li v-if="contactPhones.length" class="flex items-start gap-2">
            <IconFbochas name="phone" :size="14" class="mt-1 flex-shrink-0" />
            <div class="flex min-w-0 flex-col items-start gap-1.5">
              <a
                v-for="phone in contactPhones"
                :key="phone"
                :href="`tel:${phone.replace(/[^+\d]/g, '')}`"
                class="footer-link leading-relaxed"
              >
                {{ phone }}
              </a>
            </div>
          </li>
          <li class="flex items-center gap-2">
            <IconFbochas name="mail" :size="14" />
            <a
              :href="`mailto:${data.contact?.email}`"
              class="footer-link min-w-0 break-all"
            >
              {{ data.contact?.email }}
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div
      class="mx-auto mt-10 max-w-7xl border-t border-bochas-card-border pt-6 text-center text-base font-normal text-bochas-text-muted">
      <p>© 2026 Federación de Bochas de Córdoba. Todos los derechos reservados.</p>
      <a
        class="mt-3 inline-flex items-center gap-1 text-xs"
        href="https://wa.me/5493564631834?text=Hola%2C%20estoy%20visitando%20la%20web%20de%20Bochas%20C%C3%B3rdoba%20y%20quisiera%20comunicarme%20con%20el%20equipo%20de%20Alquimia."
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>Tecnología de</span>
        <img
          :src="alquimiaLogo"
          alt="ALQUIMIA"
          width="55"
          height="13"
          class="h-[13px] w-[55px] object-contain"
          loading="lazy"
          decoding="async"
        />
      </a>
    </div>
  </footer>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import IconFbochas from "../Icons/IconFbochas.vue";
import alquimiaLogo from "@/assets/images/alquimia-oscuro.png";
import {
  createFooterFallback,
  getFooterData,
} from "@/services/landing/landingService";
import { obtenerFederationContent } from "@/services/federation/federationContentService";
import { obtenerSiteSettingsMap } from "@/services/siteSettings/siteSettingsService";
import {
  crearSiteVisibility,
  obtenerSiteVisibility,
} from "@/services/siteSettings/siteVisibilityService";

const data = ref(createFooterFallback());
const visibility = ref(crearSiteVisibility());
const pageVisibilityByPath = {
  "/calendario": "pages.calendario",
  "/resultados": "pages.resultados",
  "/noticias": "pages.noticias",
  "/historias": "pages.historias",
  "/alojamiento": "pages.alojamiento",
  "/la-federacion": "pages.federacion",
};
const linkIsVisible = (link) => {
  const path = Object.keys(pageVisibilityByPath).find((item) =>
    String(link.to || "").startsWith(item),
  );
  return !path || visibility.value[pageVisibilityByPath[path]] !== false;
};
const quickLinks = computed(() => (data.value.quickLinks || []).filter(linkIsVisible));
const institutionalLinks = computed(() =>
  visibility.value["pages.federacion"] === false ||
  visibility.value["federacion.reglamentacion"] === false
    ? []
    : data.value.institutional || [],
);
const contactPhones = computed(() =>
  String(data.value.contact?.phone || "")
    .split(/[\n,;|/]+/)
    .map((phone) => phone.trim())
    .filter(Boolean),
);

onMounted(async () => {
  const [footer, federation, savedVisibility, settings] = await Promise.all([
    getFooterData(),
    obtenerFederationContent(),
    obtenerSiteVisibility(),
    obtenerSiteSettingsMap(),
  ]);
  data.value = footer;
  data.value.about.title = settings["footer.aboutTitle"] || footer.about.title;
  data.value.about.description =
    settings["footer.aboutDescription"] || footer.about.description;
  data.value.contact.address =
    settings["footer.address"] || footer.contact.address;
  data.value.contact.phone = settings["footer.phone"] || footer.contact.phone;
  data.value.contact.email = settings["footer.email"] || footer.contact.email;
  visibility.value = savedVisibility;
  if (federation.documents?.length) {
    data.value.institutional = federation.documents.map((doc) => ({
      label: doc.titulo,
      icon: "doc",
      url: doc.url,
      to: "/la-federacion#reglamentacion",
    }));
  }
});
</script>

<style scoped>
.footer-link {
  transition: color 200ms ease, transform 200ms ease;
}

.footer-link:hover {
  color: #a52121;
  transform: translateX(3px);
}

.footer-link:focus-visible {
  outline: 2px solid #a52121;
  outline-offset: 3px;
  border-radius: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .footer-link {
    transition: none;
  }

  .footer-link:hover {
    transform: none;
  }
}
</style>
